import React from "react";
import { Clock, MapPin, Flag } from "lucide-react";
import { schedule } from "../mock";
import { useReveal } from "../hooks/useReveal";
import { useSettings } from "../context/SettingsContext";

export default function Schedule() {
  const { ref, inView } = useReveal();
  const settings = useSettings();
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(new Date());
  const fixtures = (settings?.fixtures || [])
    .filter((f) => f && f.date && f.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 6);
  const fmt = (d, opt) => new Date(`${d}T00:00:00`).toLocaleDateString("en-US", opt);
  return (
    <section id="schedule" className="relative py-24 bg-[#0d0d0f]">
      <div className="max-w-7xl mx-auto px-5">
        <div ref={ref} className={`reveal ${inView ? "in-view" : ""} text-center max-w-2xl mx-auto`}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="h-[2px] w-10 bg-gold" />
            <span className="section-label text-gold text-xs">Training Schedule</span>
            <span className="h-[2px] w-10 bg-gold" />
          </div>
          <h2 className="display-font uppercase text-white text-4xl sm:text-5xl leading-[0.95]">
            Weekly club <span className="text-gold">rhythm.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-14 max-w-4xl mx-auto">
          {schedule.map((s) => (
            <div
              key={s.day}
              className="relative bg-card border border-white/10 rounded-lg p-8 text-center hover:border-gold/50 transition-colors group"
            >
              <div className="cond-font uppercase tracking-widest text-gold text-sm mb-3">{s.type}</div>
              <div className="display-font uppercase text-3xl text-white">{s.day}</div>
              <div className="flex items-center justify-center gap-2 mt-4 text-white/60">
                <Clock size={16} className="text-gold" />
                <span className="font-light">{s.time}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center gap-2 mt-10 text-white/50">
          <MapPin size={16} className="text-gold" />
          <span className="cond-font uppercase tracking-widest text-sm">South Florida · Training Grounds</span>
        </div>

        {fixtures.length > 0 && (
          <div className="mt-16 max-w-3xl mx-auto" data-testid="upcoming-fixtures">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Flag size={16} className="text-gold" />
              <span className="cond-font uppercase tracking-widest text-gold text-sm">Upcoming Fixtures</span>
            </div>
            <div className="divide-y divide-white/10 border border-white/10 rounded-lg overflow-hidden">
              {fixtures.map((f, i) => (
                <div key={f.date || i} className="flex items-center justify-between px-5 py-4 bg-card" data-testid={`fixture-row-${i}`}>
                  <div className="flex items-center gap-4">
                    <div className="text-center w-14 shrink-0">
                      <div className="display-font text-gold text-2xl leading-none">{fmt(f.date, { day: "2-digit" })}</div>
                      <div className="cond-font uppercase text-white/50 text-[11px] tracking-widest">{fmt(f.date, { month: "short" })}</div>
                    </div>
                    <div>
                      <div className="cond-font uppercase text-white tracking-wide">{f.home ? "vs" : "@"} {f.opponent || "TBD"}</div>
                      <div className="text-white/50 text-sm font-light">{f.time ? f.time : "Kickoff TBD"}</div>
                    </div>
                  </div>
                  <span className={`cond-font uppercase text-[11px] tracking-widest px-3 py-1 rounded-full border ${f.home ? "border-gold/40 text-gold" : "border-white/20 text-white/60"}`}>{f.home ? "Home" : "Away"}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
